/**
 * Partner and Referral Enums
 *
 * Enums for partner management, referrals, and commissions.
 * Issue #313 - Centralize enums to shared-types
 *
 * Note: Some types (CommissionStatus, ReferralStatus) are defined in
 * dtos/consumer.dto.ts as Zod schemas. These should eventually be
 * migrated to use z.nativeEnum() from this file.
 */

import { z } from 'zod';

// ============================================================================
// PARTNER TYPE
// ============================================================================

/**
 * Partner types for B2B partnerships.
 */
export enum PartnerType {
  /** Referral partner — partner refers clients, borrower sees the lender brand. */
  REFERRAL = 'referral',
  /**
   * White-label (formerly "Channel"/"Wholesale") — partner's client does NOT
   * see the lender's brand; partner keeps borrowers on their own account and
   * branding; lender processes behind the scenes.
   */
  WHITE_LABEL = 'white_label',
}

/** Zod schema for PartnerType validation */
export const PartnerTypeSchema = z.nativeEnum(PartnerType);

/**
 * Input schema for partnerType fields at API boundaries. Accepts the
 * template's uppercase form (e.g. `'WHITE_LABEL'` from radio-card values) and
 * normalizes to the canonical lowercase enum value before validating.
 */
export const PartnerTypeInputSchema = z
  .string()
  .min(1)
  .transform((v) => v.toLowerCase())
  .pipe(PartnerTypeSchema);

/** Array of all partner types for iteration */
export const PARTNER_TYPES = Object.values(PartnerType);

// ============================================================================
// PARTNER STATUS
// ============================================================================

/**
 * Partner account status
 */
export enum PartnerStatus {
  /** Partner is being verified */
  PENDING_VERIFICATION = 'pending_verification',
  /** Partner is active and approved */
  ACTIVE = 'active',
  /** Partner is inactive (voluntarily) */
  INACTIVE = 'inactive',
  /** Partner is suspended (by admin) */
  SUSPENDED = 'suspended',
}

/** Zod schema for PartnerStatus validation */
export const PartnerStatusSchema = z.nativeEnum(PartnerStatus);

/** Array of all partner statuses for iteration */
export const PARTNER_STATUSES = Object.values(PartnerStatus);

// ============================================================================
// PARTNER INVITATION STATUS (Issue #484)
// ============================================================================

/**
 * Partner invitation lifecycle status
 *
 * @see Issue #484 - B1-to-B2 Partner Invitation & Onboarding Flow
 */
export enum PartnerInvitationStatus {
  /** Created, not sent */
  PENDING = 'PENDING',
  /** Email sent */
  SENT = 'SENT',
  /** Link clicked */
  VIEWED = 'VIEWED',
  /** Wizard started */
  ONBOARDING = 'ONBOARDING',
  /** Company created, waiting for e-sign */
  PENDING_SIGNATURE = 'PENDING_SIGNATURE',
  /** Fully completed */
  ACCEPTED = 'ACCEPTED',
  /** Rejected by invitee */
  DECLINED = 'DECLINED',
  /** Past expiresAt */
  EXPIRED = 'EXPIRED',
  /** Revoked by B1 */
  CANCELLED = 'CANCELLED',
}

/** Zod schema for PartnerInvitationStatus validation */
export const PartnerInvitationStatusSchema = z.nativeEnum(PartnerInvitationStatus);

/** Array of all partner invitation statuses for iteration */
export const PARTNER_INVITATION_STATUSES = Object.values(PartnerInvitationStatus);

/** Statuses where B1 can still edit the invitation details (contact, terms, etc.). */
export const EDITABLE_INVITATION_STATUSES: ReadonlySet<PartnerInvitationStatus> = new Set([
  PartnerInvitationStatus.PENDING,
  PartnerInvitationStatus.SENT,
  PartnerInvitationStatus.VIEWED,
  PartnerInvitationStatus.ONBOARDING,
]);

/** Non-terminal statuses — the invite is still "in flight" (show as Invited in UI).
 *  Superset of EDITABLE: includes PENDING_SIGNATURE where the invite is active
 *  but editing is locked because signatures are in progress. */
export const NON_TERMINAL_INVITATION_STATUSES: ReadonlySet<PartnerInvitationStatus> = new Set([
  PartnerInvitationStatus.PENDING,
  PartnerInvitationStatus.SENT,
  PartnerInvitationStatus.VIEWED,
  PartnerInvitationStatus.ONBOARDING,
  PartnerInvitationStatus.PENDING_SIGNATURE,
]);

// Note: ReferralStatus and ReferralStatusSchema are defined in dtos/consumer.dto.ts
// They use ['submitted', 'contacted', 'qualified', 'converted', 'lost']
// This should be consolidated in a future refactoring effort.

// ============================================================================
// EXTENDED REFERRAL STATUS
// ============================================================================

/**
 * Extended referral status (consumer portal)
 * Includes additional stages like term_sheet and funded
 */
export enum ExtendedReferralStatus {
  NEW = 'new',
  CONTACTED = 'contacted',
  QUALIFIED = 'qualified',
  TERM_SHEET = 'term_sheet',
  FUNDED = 'funded',
  DECLINED = 'declined',
}

/** Zod schema for ExtendedReferralStatus validation */
export const ExtendedReferralStatusSchema = z.nativeEnum(ExtendedReferralStatus);

// ============================================================================
// REFERRAL TYPE
// ============================================================================

/**
 * Type of referral (loan purpose)
 */
export enum ReferralType {
  BUYER = 'buyer',
  SELLER = 'seller',
  INVESTOR = 'investor',
  REFINANCE = 'refinance',
}

/** Zod schema for ReferralType validation */
export const ReferralTypeSchema = z.nativeEnum(ReferralType);

/** Array of all referral types for iteration */
export const REFERRAL_TYPES = Object.values(ReferralType);

// Note: CommissionStatus and CommissionStatusSchema are defined in dtos/consumer.dto.ts
// They use ['pending', 'approved', 'paid', 'cancelled']
// This should be consolidated in a future refactoring effort.

// ============================================================================
// DEFAULT PARTNER CONTRACT TERMS
// ============================================================================

/**
 * Default contract terms used when creating a partner invitation without
 * explicit terms. Shared between intake conversion and invitation creation
 * fallback paths.
 *
 * Note: The gRPC payload mapping (snake_case) stays in each router —
 * only the logical values are shared here.
 */
export const DEFAULT_PARTNER_CONTRACT_TERMS = {
  commissionType: 'percentage',
  commissionRate: 2,
  commissionTrigger: 'funded',
  paymentSchedule: 'MONTHLY',
  agreementDuration: '12',
} as const;

// ============================================================================
// PAYOUT SCHEDULE
// ============================================================================

/**
 * Commission payout schedule
 */
export enum PayoutSchedule {
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
  MONTHLY = 'MONTHLY',
}

/** Zod schema for PayoutSchedule validation */
export const PayoutScheduleSchema = z.nativeEnum(PayoutSchedule);

// ============================================================================
// SOURCE TYPE
// ============================================================================

/**
 * Lead/referral source types
 */
export enum SourceType {
  PARTNER = 'PARTNER',
  AFFILIATE = 'AFFILIATE',
  INFLUENCER = 'INFLUENCER',
  CAMPAIGN = 'CAMPAIGN',
  REFERRAL = 'REFERRAL',
}

/** Zod schema for SourceType validation */
export const SourceTypeSchema = z.nativeEnum(SourceType);

/** Array of all source types for iteration */
export const SOURCE_TYPES = Object.values(SourceType);
